import { type AskPunttStructuredContent } from "@mg/schemas/src/christo/askPuntt";
import {
  Check,
  CheckSquare,
  Clock,
  Copy,
  Warning,
} from "@phosphor-icons/react";
import { Badge, Button, Flex, Separator, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";

import { useAnalytics } from "../../../utils/analytics";

interface StructuredResponseProps {
  content: AskPunttStructuredContent;
  /** Controls progressive reveal: 1=risk+shortAnswer, 2=+alternatives, 3=+analysis, 4=+timeSaved. Undefined=show all */
  maxSections?: number;
  /** Parent message id — required for unique data-testid per message */
  messageId: string;
}

function RiskBadge({
  level,
  confidence,
}: {
  level: string;
  confidence?: string;
}) {
  const colors: Record<string, { bg: string; icon: string; text: string }> = {
    low: {
      bg: "bg-malachite-100",
      icon: "text-malachite-700",
      text: "Low Risk",
    },
    medium: {
      bg: "bg-puntt-gold-3",
      icon: "text-puntt-gold-11",
      text: "Medium Risk",
    },
    high: { bg: "bg-puntt-red-3", icon: "text-puntt-red-9", text: "High Risk" },
  };

  const color = colors[level] || colors.medium;

  return (
    <div
      className={`mb-3 flex items-center gap-2 rounded-xl p-3 ${color.bg}`}
      data-testid="risk-level-badge"
    >
      <Warning size={24} weight="fill" className={color.icon} />
      <Text size="5" weight="bold" className="text-puntt-neutral-gray-12">
        {color.text}
        {confidence &&
          ` | ${confidence.charAt(0).toUpperCase() + confidence.slice(1)} Confidence`}
      </Text>
    </div>
  );
}

function SourceBadge({ label }: { label: string }) {
  return (
    <Badge
      size="1"
      variant="surface"
      color="gray"
      className="inline-flex cursor-default"
    >
      {label}
    </Badge>
  );
}

function AlternativeCard({
  alternative,
}: {
  alternative: {
    riskLevel: string;
    claim: string;
    why?: string;
    precedent?: string;
    status?: string;
    approvalPath?: string;
    tradeOff?: string;
  };
}) {
  const levelStyles: Record<
    string,
    { bg: string; text: string; icon: string }
  > = {
    low: {
      bg: "bg-malachite-100",
      text: "text-malachite-700",
      icon: "text-malachite-700",
    },
    medium: {
      bg: "bg-puntt-gold-3",
      text: "text-puntt-gold-11",
      icon: "text-puntt-gold-11",
    },
    high: {
      bg: "bg-puntt-red-3",
      text: "text-puntt-red-9",
      icon: "text-puntt-red-9",
    },
  };

  const style = levelStyles[alternative.riskLevel] || levelStyles.medium;

  return (
    <div className="rounded-xl border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 p-3">
      <Flex align="center" gap="2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 ${style.bg}`}
        >
          <CheckSquare size={14} weight="fill" className={style.icon} />
          <Text size="1" weight="bold" className={style.text}>
            {alternative.riskLevel.toUpperCase()} RISK
          </Text>
        </span>
        <Text size="2" className="text-puntt-neutral-gray-12">
          &quot;{alternative.claim}&quot;
        </Text>
      </Flex>
      <div className="ml-6 mt-1 space-y-0.5">
        {alternative.why && (
          <Text as="p" size="1" className="text-puntt-neutral-gray-11">
            <span className="italic">Why:</span> {alternative.why}
          </Text>
        )}
        {alternative.precedent && (
          <Text as="p" size="1" className="text-puntt-neutral-gray-11">
            <span className="italic">Precedent:</span> {alternative.precedent}
          </Text>
        )}
        {alternative.status && (
          <Text as="p" size="1" className="text-puntt-neutral-gray-11">
            <span className="italic">Status:</span> {alternative.status}
          </Text>
        )}
        {alternative.approvalPath && (
          <Text as="p" size="1" className="text-puntt-neutral-gray-11">
            <span className="italic">Approval path:</span>{" "}
            {alternative.approvalPath}
          </Text>
        )}
        {alternative.tradeOff && (
          <Text as="p" size="1" className="text-puntt-neutral-gray-11">
            <span className="italic">Trade-off:</span> {alternative.tradeOff}
          </Text>
        )}
      </div>
    </div>
  );
}

export function StructuredResponse({
  content,
  maxSections,
  messageId,
}: StructuredResponseProps) {
  const showAll = maxSections === undefined;
  const analytics = useAnalytics();
  const hasTrackedView = useRef(false);
  const revealedRef = useRef<Set<number>>(new Set());
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (
      (showAll || (maxSections !== undefined && maxSections >= 4)) &&
      !hasTrackedView.current
    ) {
      hasTrackedView.current = true;
      analytics.capture("ask_puntt_structured_response_viewed", {
        risk_level: content.riskLevel,
        confidence: content.confidence,
        alternatives_count: content.alternatives?.length ?? 0,
        time_saved: content.timeSaved?.summary,
      });
    }
  }, [showAll]);

  return (
    <div
      className="space-y-4 rounded-2xl border border-puntt-neutral-gray-6 bg-base-white p-5 shadow-sm"
      data-testid={`structured-response-${messageId}`}
    >
      {/* Section 1: Risk Badge + Short Answer */}
      {(showAll || (maxSections !== undefined && maxSections >= 1)) &&
        content.riskLevel && (
          <div
            className={
              !revealedRef.current.has(1) && !showAll
                ? "animate-block-fade-up"
                : ""
            }
            ref={() => revealedRef.current.add(1)}
          >
            <RiskBadge
              level={content.riskLevel}
              confidence={content.confidence}
            />
          </div>
        )}

      {(showAll || (maxSections !== undefined && maxSections >= 1)) &&
        content.shortAnswer && (
          <Text
            as="p"
            size="2"
            className="border-l-2 border-puntt-accent-7 pl-3 text-puntt-neutral-gray-12"
          >
            Short answer: {content.shortAnswer}
          </Text>
        )}

      {/* Section 2: Alternatives */}
      {(showAll || (maxSections !== undefined && maxSections >= 2)) &&
        content.alternatives &&
        content.alternatives.length > 0 && (
          <>
            <Separator size="4" />
            <Flex align="center" gap="2">
              <CheckSquare
                size={18}
                weight="fill"
                className="text-malachite-700"
              />
              <Text
                size="3"
                weight="bold"
                className="text-puntt-neutral-gray-12"
              >
                Safe Alternatives with Verified Precedents
              </Text>
            </Flex>
            <div className="space-y-4">
              {content.alternatives.map((alt, idx) => (
                <AlternativeCard key={idx} alternative={alt} />
              ))}
            </div>
          </>
        )}

      {/* Section 3: Regulatory Analysis */}
      {(showAll || (maxSections !== undefined && maxSections >= 3)) &&
        content.regulatoryAnalysis && (
          <>
            <Separator size="4" />
            <Text size="3" weight="bold" className="text-puntt-neutral-gray-12">
              Justification &amp; Regulatory Analysis
            </Text>
            <div className="space-y-2 leading-relaxed text-puntt-neutral-gray-12">
              {content.regulatoryAnalysis
                .split("\n\n")
                .map((paragraph, idx) => (
                  <div key={idx}>
                    {paragraph.startsWith("**") ? (
                      <Text as="p" size="2" weight="bold" className="mb-1">
                        {paragraph.replace(/\*\*/g, "")}
                      </Text>
                    ) : paragraph.startsWith("- ") ? (
                      <ul className="ml-4 list-disc space-y-1">
                        {paragraph.split("\n").map((line, lineIdx) => (
                          <li key={lineIdx}>
                            <Text size="2">{line.replace(/^- /, "")}</Text>
                            {content.sources?.map((source) =>
                              line
                                .toLowerCase()
                                .includes(
                                  source.label.toLowerCase().split(" ")[0],
                                ) ? (
                                <SourceBadge
                                  key={source.label}
                                  label={source.label}
                                />
                              ) : null,
                            )}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <Text as="p" size="2">
                        {paragraph}
                      </Text>
                    )}
                  </div>
                ))}
            </div>
          </>
        )}

      {/* Section 4: Time Saved */}
      {(showAll || (maxSections !== undefined && maxSections >= 4)) &&
        content.timeSaved && (
          <>
            <Separator size="4" />
            <Flex align="center" gap="2">
              <Clock size={18} className="text-puntt-neutral-gray-11" />
              <Text
                size="3"
                weight="bold"
                className="text-puntt-neutral-gray-12"
              >
                TIME SAVED: {content.timeSaved.summary}
              </Text>
            </Flex>
            <Text as="p" size="2" className="text-puntt-neutral-gray-11">
              If submitted as-is:
            </Text>
            <div className="ml-2 space-y-1 rounded-lg bg-puntt-gold-2/80 p-3">
              {content.timeSaved.breakdown.map((item, idx) => (
                <Text
                  as="p"
                  key={idx}
                  size="2"
                  className="text-puntt-neutral-gray-12"
                >
                  →{" "}
                  <Text as="span" weight="bold">
                    {item.week}:
                  </Text>{" "}
                  {item.description}
                </Text>
              ))}
            </div>
          </>
        )}

      {/* Action Buttons */}
      <Flex gap="2" className="mt-2">
        <Button
          data-testid="structured-response-button-328"
          variant="ghost"
          color="gray"
          size="1"
          className="cursor-pointer"
          aria-label="Copy response"
          onClick={() => {
            const textParts: string[] = [];
            if (content.shortAnswer) textParts.push(content.shortAnswer);
            if (content.regulatoryAnalysis)
              textParts.push(content.regulatoryAnalysis);
            if (content.alternatives?.length) {
              textParts.push(
                content.alternatives
                  .map(
                    (a) =>
                      `${a.riskLevel.toUpperCase()}: "${a.claim}"${a.why ? ` — ${a.why}` : ""}`,
                  )
                  .join("\n"),
              );
            }
            navigator.clipboard.writeText(textParts.join("\n\n"));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            analytics.capture("ask_puntt_response_copy", {
              risk_level: content.riskLevel,
            });
          }}
        >
          {copied ? (
            <Check size={14} className="text-malachite-700" />
          ) : (
            <Copy size={14} />
          )}
        </Button>
      </Flex>
    </div>
  );
}
